import { useContext, useState, useEffect } from "react"
import { UserContext } from "../App"
import { MDBCard, MDBCardBody, MDBCardTitle, MDBCol, MDBRow, MDBCardText, MDBCheckbox, MDBListGroup, MDBListGroupItem, MDBModal, MDBModalDialog, MDBModalContent, MDBModalHeader, MDBModalTitle, MDBBtn, MDBModalBody, MDBModalFooter, MDBCardImage, MDBDropdownItem, MDBSpinner } from "mdb-react-ui-kit";
import { useTranslation } from "react-i18next";
import { generateClient } from 'aws-amplify/api';
import { signOut } from "aws-amplify/auth";
import { captureError } from "../utils/sentryHelpers";

import EmploymentRequests from "./dashboard/EmploymentRequests";
import { Link } from "react-router-dom";
import { getLocalizedText } from "../utils/localizedText";
import { useIndustry } from "../utils/industryContextHook";
import ShowTicket from "../components-user/tickets/ShowTicket";
import { AdminOnboardingModal } from "../components/admin-onboarding";
import { AdminOnboardingV2Modal } from "../components/admin-onboarding-v2";

const client = generateClient();

function isoToGermanDate(isoString) {
    // Parse the ISO string into a Date object
    const date = new Date(isoString);

    // Format the date as DD.MM.YYYY
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // January is 0
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
}

export default function Dashboard() {
    const { t, i18n } = useTranslation();
    const { user, setUserCompletedOnboarding} = useContext(UserContext);
    const { currentIndustry} = useIndustry();

    // Check if user is company admin
    const isCompanyAdmin = user?.employeeProfiles?.items?.some(
        profile => profile.role === 'admin'
    );
    const adminCompanyId = user?.employeeProfiles?.items?.find(
        profile => profile.role === 'admin'
    )?.company?.id;

    // Admin Onboarding Modal (V1 - legacy)
    const [showAdminOnboardingModal, setShowAdminOnboardingModal] = useState(false);

    // Admin Onboarding V2 Modal
    const [showAdminOnboardingV2Modal, setShowAdminOnboardingV2Modal] = useState(false);

    // Ticket Modal
    const [showTicketModal, setShowTicketModal] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [loadingTickets, setLoadingTickets] = useState(false);
    
    const toggleTicketModal = () => setShowTicketModal(!showTicketModal);
   

    const updateUserCompletedOnboarding = async () => {
        try {
            const userRes = await client.graphql({
                query: /* GraphQL */ `
                mutation UpdateUser(
                  $input: UpdateUserInput!
                  $condition: ModelUserConditionInput
                ) {
                  updateUser(input: $input, condition: $condition) {
                    id
                    hasCompletedOnboarding
                  }
                }
              `,
                variables: { input: {
                    id: user.id,
                    hasCompletedOnboarding: true
                } }
            });
        setUserCompletedOnboarding(true);
        } catch (error) {
            console.warn("error updating user", error);
            captureError(error, {
                feature: 'dashboard',
                operation: 'update-user-onboarding-status',
                context: {
                    userId: user?.id
                },
                tags: { component: 'Dashboard-User-Admin' }
            });
        }
    }

    // Handle admin onboarding completion (V1 - legacy)
    const handleAdminOnboardingComplete = async () => {
        console.log('Admin onboarding completed for company:', adminCompanyId);
        await updateUserCompletedOnboarding();
        setShowAdminOnboardingModal(false);
    };

    // Handle admin onboarding V2 completion
    const handleAdminOnboardingV2Complete = () => {
        console.log('Admin onboarding V2 completed for company:', adminCompanyId);
        setShowAdminOnboardingV2Modal(false);
    };

    const [news, setNews] = useState([]);
    const [loadingNews, setLoadingNews] = useState(false);

    const fetchNews = async () => {
        setLoadingNews(true);
        try {
            const newsData = await client.graphql({
                query: /* GraphQL */ `
                query ListCMSNews(
                  $filter: ModelCMSNewsFilterInput
                  $limit: Int
                  $nextToken: String
                ) {
                  listCMSNews(filter: $filter, limit: $limit, nextToken: $nextToken) {
                    items {
                        id
                        type
                        approved
                        status
                        createdAt
                        image {
                            fileName
                            alt
                        }
                        name {
                            language
                            text
                        }
                        excerpt {
                            language
                            text
                        }
                        __typename
                  }
                }
            }
              `,
                variables: {
                    limit: 10,
                    filter: {
                        approved: { eq: true },
                        status: { eq: "checked" }
                    }
                }
            });

            // Add null safety checks before accessing nested properties
            if (!newsData?.data?.listCMSNews?.items) {
                console.log('No news data available');
                setNews([]);
                return;
            }

            const newsList = newsData.data.listCMSNews.items
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            setNews(newsList);
        } catch (error) {
            console.log('error on fetching news', error);
            captureError(error, {
                feature: 'dashboard',
                operation: 'fetch-news',
                context: { companyId: user?.employeeProfiles?.items?.find(profile => profile.role === 'admin')?.company?.id },
                tags: { component: 'Dashboard-User-Admin' }
            });
            setNews([]);
        } finally {
            setLoadingNews(false);
        }
    };

    const [events, setEvents] = useState([]);
    const [userTickets, setUserTickets] = useState([]);
    // Map ticketId → OrderFinancial.paymentProvider ("stripe" | "invoice"). Built from
    // the user's orders so we can decide the "Pay ticket" CTA per booking instead of
    // relying on the mutable event-level instantPaymentOnly toggle.
    const [paymentProviderByTicketId, setPaymentProviderByTicketId] = useState(new Map());

    // Fetch user tickets
    const fetchUserTickets = async () => {
        if (!user.id) return;

        setLoadingTickets(true);
        try {
            const ticketsData = await client.graphql({
                query: /* GraphQL */ `
                query GetUser($id: ID!) {
                  getUser(id: $id) {
                    id
                    owner
                    tickets {
                      items {
                        id
                        ticketEventId
                        status
                        paymentStatus
                        eventDays
                        event {
                          id
                          name {
                            language
                            text
                          }
                          startDate
                          endDate
                          instantPaymentOnly
                        }
                      }
                    }
                    orders {
                      items {
                        id
                        financial { paymentProvider }
                        lineItems {
                          items {
                            bookedTicket { id }
                          }
                        }
                      }
                    }
                  }
                }
                `,
                variables: { id: user.id }
            });

            // Add null safety checks before accessing nested properties
            if (!ticketsData?.data?.getUser?.tickets?.items) {
                console.log('No tickets data available');
                setUserTickets([]);
                return;
            }

            const ticketsList = ticketsData.data.getUser.tickets.items;
            const ordersList = ticketsData.data.getUser.orders?.items ?? [];
            const providerMap = new Map();
            ordersList.forEach((order) => {
                const provider = order.financial?.paymentProvider;
                if (!provider) return;
                order.lineItems?.items?.forEach((li) => {
                    const ticketId = li.bookedTicket?.id;
                    if (ticketId) providerMap.set(ticketId, provider);
                });
            });
            console.log('User tickets', ticketsList);
            setUserTickets(ticketsList);
            setPaymentProviderByTicketId(providerMap);
        } catch (error) {
            console.log('Error fetching user tickets', error);
            captureError(error, {
                feature: 'dashboard',
                operation: 'fetch-user-tickets',
                context: {
                    userId: user?.id
                },
                tags: { component: 'Dashboard-User-Admin' }
            });
            setUserTickets([]);
        } finally {
            setLoadingTickets(false);
        }
    };

    const fetchEvents = async (currentIndustry) => {
        try {
            const eventData = await client.graphql({
                query: /* GraphQL */ `
                query ListIndustryEvents(
                  $filter: ModelIndustryEventsFilterInput
                  $limit: Int
                ) {
                  listIndustryEvents(filter: $filter, limit: $limit) {
                    items {
                    event {
                      id
                      name {
                        language
                        text
                        }
                      image {
                        fileName
                        alt
                      }
                      location {
                        address {
                            city
                        }
                      }
                      status
                      date
                      startDate
                      endDate
                      enableTicketBooking
                      landingPageUrl

                      createdAt
                      updatedAt
                      __typename
                    }
                    __typename
                  }
                }
                }
              `,
                variables: { filter: { industryId: { eq: currentIndustry.id }} }
            });

            // Add null safety checks before accessing nested properties
            if (!eventData?.data?.listIndustryEvents?.items) {
                console.log('No event data available');
                setEvents([]);
                return;
            }

            const eventList = eventData.data.listIndustryEvents.items.map(e => e.event)
            .filter(e => {
                if (e.status !== "active" || !e.endDate) return false;
                const endDate = new Date(e.endDate);
                endDate.setHours(23, 59, 59, 999);
                return endDate >= new Date();
            })
            .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
            console.log('event list', eventList);
            setEvents(eventList);
        } catch (error) {
            console.log('error on fetching events', error);
            captureError(error, {
                feature: 'dashboard',
                operation: 'fetch-events',
                context: {
                    industryId: currentIndustry?.id
                },
                tags: { component: 'Dashboard-User-Admin' }
            });
            setEvents([]);
        }
    }

    // Helper function to check if user has a ticket for an event
    const hasTicketForEvent = (eventId) => {
        return userTickets.some(ticket =>
            ticket.event.id === eventId &&
            ticket.status !== "cancelled" &&
            ticket.paymentStatus !== "refunded"
        );
    };

    // Check if the ticket for an event has a pending/unpaid payment
    const getTicketForEvent = (eventId) => {
        return userTickets.find(t => t.event.id === eventId && t.status !== "cancelled");
    };

    const isTicketUnpaid = (eventId) => {
        const ticket = getTicketForEvent(eventId);
        if (!ticket) return false;
        const paymentPending = ticket.paymentStatus === "unpaid" || ticket.paymentStatus === "pending";
        if (!paymentPending) return false;
        // Per-booking truth first (immutable), fall back to the event's current config
        // for legacy/free tickets without a linked order.
        const provider = paymentProviderByTicketId.get(ticket.id)
            ?? (ticket.event?.instantPaymentOnly === true ? "stripe" : "invoice");
        return provider === "stripe";
    };

    // Handler to show ticket
    const handleShowTicket = (eventId) => {
        const ticket = userTickets.find(t => t.event.id === eventId);
        if (ticket) {
            setSelectedTicket(ticket);
            setShowTicketModal(true);
        }
    };

    // Handler to delete ticket
    const handleDeleteTicket = () => {
        // Remove the ticket from the local state
        setUserTickets(userTickets.filter(t => t.id !== selectedTicket.id));
        setShowTicketModal(false);
        // No need to reload the tickets as we've already updated the state
    };

    const [todos, setTodos] = useState([]);

    const fetchTodos = async () => {
        try {
            const todoData = await client.graphql({
                query: /* GraphQL */ `
                query ListToDos(
                  $filter: ModelToDoFilterInput
                  $limit: Int
                  $nextToken: String
                ) {
                  listToDos(filter: $filter, limit: $limit, nextToken: $nextToken) {
                    items {
                            id
                            user {
                                id
                            }
                            company {
                                id
                                name
                                image {
                                    main {
                                        fileName
                                        alt
                                    }
                                }
                            }
                            title
                            description
                            status
                            dueDate
                            startDate
                            priority
                            createdBy {
                                id
                            }
                            createdAt
                            updatedAt
                            __typename
                        }
                    }
                }
                `,
                variables: { filter: (() => {
                    const companyFilters = (user.employeeProfiles?.items ?? [])
                        .filter(e => e.company?.id && typeof e.company.id === 'string' && e.company.id.trim() !== '')
                        .map(e => ({ companyTodosId: { eq: e.company.id } }));
                    const orFilters = [
                        { userTodosId: { eq: user.id } },
                        ...companyFilters
                    ];
                    // DynamoDB "or" with only 1 element causes syntax error, so use the filter directly
                    return orFilters.length > 1 ? { or: orFilters } : orFilters[0];
                })() }
            });

            // Add null safety checks before accessing nested properties
            if (!todoData?.data?.listToDos?.items) {
                console.log('No todos data available');
                setTodos([]);
                return;
            }

            const todoList = todoData.data.listToDos.items.filter(t => t.company.id).sort((a, b) => a.priority - b.priority);;
            console.log('todo list', todoList);
            setTodos(todoList);
        } catch (error) {
            console.log('error on fetching todos', error);
            captureError(error, {
                feature: 'dashboard',
                operation: 'fetch-todos',
                context: {
                    userId: user?.id
                },
                tags: { component: 'Dashboard-User-Admin' }
            });
            setTodos([]);
        }
    }

    // Update Todo in backend when checked or unchecked
    const updateTodo = async (todo) => {
        try {
            const todoData = await client.graphql({
                query: /* GraphQL */ `
                mutation UpdateToDo(
                  $input: UpdateToDoInput!
                  $condition: ModelToDoConditionInput
                ) {
                  updateToDo(input: $input, condition: $condition) {
                        id
                        user {
                            id
                        }
                        company {
                            id
                            name
                            image {
                                main {
                                    fileName
                                    alt
                                }
                            }
                        }
                        title
                        description
                        status
                        dueDate
                        priority
                        createdBy {
                            id
                        }
                        createdAt
                        updatedAt
                        __typename
                    }
                }
                `,
                variables: {
                    input: {
                        id: todo.id,
                        status: todo.status
                    }
                }
            });
            const todoList = [...todos];
            const todoIndex = todoList.findIndex(t => t.id === todo.id);
            todoList[todoIndex] = todoData.data.updateToDo.sort((a, b) => a.priority - b.priority);
            setTodos(todoList);
        } catch (error) {
            console.log('error on updating todo', error);
            captureError(error, {
                feature: 'dashboard',
                operation: 'update-todo',
                context: {
                    todoId: todo?.id,
                    status: todo?.status
                },
                tags: { component: 'Dashboard-User-Admin' }
            });
        }
    }

    const handleCheckTodo = (i) => {
        const newTodos = [...todos];
        newTodos[i].status = newTodos[i].status === "completed" ? "open" : "completed";
        setTodos(newTodos);
        updateTodo({
            id: newTodos[i].id,
            status: newTodos[i].status
        });
    }

    useEffect(() => {
        if (user && user.id && todos.length === 0) {
            fetchTodos();
        }
    }, [user]);
    
    useEffect(() => {
        if (user && user.id) {
            fetchUserTickets();
        }
    }, [user]);
            
    useEffect(() => {
        if(currentIndustry) {
            fetchEvents(currentIndustry);
            fetchNews();
        }
    }, [currentIndustry]);

    // Show admin onboarding V1 modal if conditions are met (legacy - disabled)
    // useEffect(() => {
    //     // Check if user has Admin cognito group
    //     const hasAdminCognitoGroup = user?.['cognito:groups']?.includes('Admin');

    //     // Only show onboarding if user is company admin, hasn't completed onboarding,
    //     // and doesn't have the Admin cognito group
    //     if (user && isCompanyAdmin && !user.hasCompletedOnboarding && !hasAdminCognitoGroup && currentIndustry?.id === "ec37b0b2-7f72-4de4-b12b-5eac5ff71ae3") {
    //         setShowAdminOnboardingModal(true);
    //     } else if (user && (user.hasCompletedOnboarding || hasAdminCognitoGroup)) {
    //         setShowAdminOnboardingModal(false);
    //     }
    // }, [user, isCompanyAdmin]);

    // Show admin onboarding V2 modal if conditions are met (temporarily disabled)
    // useEffect(() => {
    //     // Check if user has Admin cognito group
    //     const hasAdminCognitoGroup = user?.['cognito:groups']?.includes('Admin');

    //     // Check if user has completed onboarding V2
    //     const hasCompletedOnboardingV2 = user?.conditions?.some(
    //         c => c.id === "onboarding-v2-lounges-2026" && c.accepted
    //     );

    //     // Only show V2 onboarding if:
    //     // 1. User is company admin
    //     // 2. User hasn't completed V2 onboarding
    //     // 3. User doesn't have Admin cognito group (disabled for testing)
    //     // 4. Industry is cleanroom-processes
    //     if (user && isCompanyAdmin && !hasCompletedOnboardingV2 && !hasAdminCognitoGroup // TODO: Re-enable after testing
    //         && currentIndustry?.id === "ec37b0b2-7f72-4de4-b12b-5eac5ff71ae3") {
    //         setShowAdminOnboardingV2Modal(true);
    //     } else {
    //         setShowAdminOnboardingV2Modal(false);
    //     }
    // }, [user, isCompanyAdmin, currentIndustry]);

  return <>
  <span data-hj-allow className="float-end text-muted" style={{marginTop: "-50px"}}>{user.username}</span>
    <MDBRow>
        <MDBCol size="12" md="12">
            <h4>{t("Welcome to")} {getLocalizedText(currentIndustry?.name)}!</h4>
        </MDBCol>
        <EmploymentRequests />
        <MDBCol size="12" md="12">
            <MDBRow>
            {
                todos && todos.length > 0 &&
                <MDBCol size="12" className="mt-2 mt-md-0 px-0">
                <h5>{t("To do's")}</h5>
                    <MDBCard>
                        <MDBCardBody>
                            
                            <MDBRow>
                                {todos && todos.length > 0 && todos.map((todo, i) => (
                                    <MDBCol size="12" key={i}>
                                        <MDBCard className="mb-2 bg-opacity-10" background={todo.status === "completed" ? "success" : todo.startDate ? "grey" : "danger"}>
                                            <MDBCardBody className="p-2">
                                            <MDBRow>
                                            <MDBCol size="10">
                                                <MDBCardText>{todo.startDate ? t("From") + " " +  isoToGermanDate(todo.startDate) + " - " : ""}{todo.title}</MDBCardText>
                                                </MDBCol>
                                                <MDBCol size="2">
                                                {!todo.startDate && <MDBCheckbox checked={todo.status === "completed"} onChange={() => handleCheckTodo(i)} />}
                                                </MDBCol>
                                                </MDBRow>
                                            </MDBCardBody>
                                        </MDBCard>
                                    </MDBCol>
                                ))}
                            </MDBRow>
                        </MDBCardBody>
                    </MDBCard>
                </MDBCol>
            }
                
                <MDBCol size="12" className="mt-2 mt-md-3">
                <h5>{t("Upcoming Events")}</h5>
                    {loadingTickets ? (
                        <div className="text-center my-3">
                            <MDBSpinner role="status">
                                <span className="visually-hidden">{t("Loading")}</span>
                            </MDBSpinner>
                        </div>
                    ) : (
                        <MDBRow>
                            {events && events.length > 0 && events.map((event, i) => (
                                <MDBCol size="12" md="8" xl="3" key={i}>
                                    <MDBCard className="mb-2">
                                        <a href={event.landingPageUrl} target="_blank" rel="noreferrer">
                                            {event.image?.fileName && <MDBCardImage src={"https://iec-cp-public.s3.eu-central-1.amazonaws.com/EventImages/" + event.image.fileName} position='top' alt='...' />}
                                        </a>
                                        <MDBCardBody>
                                            <MDBCardTitle>{getLocalizedText(event.name)}</MDBCardTitle>
                                            <MDBCardText>
                                                {isoToGermanDate(event.startDate)} - {isoToGermanDate(event.endDate)}<br></br>
                                                {event.location?.address?.city}
                                            </MDBCardText>
                                            {event.enableTicketBooking && (
                                                hasTicketForEvent(event.id) ? (
                                                    isTicketUnpaid(event.id) ? (
                                                        <Link to={`/book-ticket?eventId=${event.id}`} state={{ event }}>
                                                            <MDBBtn color="warning">{t("Pay ticket")}</MDBBtn>
                                                        </Link>
                                                    ) : (
                                                        <MDBBtn color="success" onClick={() => handleShowTicket(event.id)}>
                                                            {t("Show ticket")}
                                                        </MDBBtn>
                                                    )
                                                ) : (
                                                    <Link to={`/book-ticket?eventId=${event.id}`} state={{ event }}>
                                                        <MDBBtn color="primary">{t("Book ticket")}</MDBBtn>
                                                    </Link>
                                                )
                                            )}
                                        </MDBCardBody>
                                    </MDBCard>
                                </MDBCol>
                            ))}
                        </MDBRow>
                    )}
                </MDBCol>
                
                {/* News Section */}
                {/* <MDBCol size="12" className="mt-4">
                    <h5>{t("News")}</h5>
                    {loadingNews ? (
                        <div className="text-center my-3">
                            <MDBSpinner role="status">
                                <span className="visually-hidden">{t("Loading")}</span>
                            </MDBSpinner>
                        </div>
                    ) : (
                        <MDBRow>
                            {news && news.length > 0 ? news.map((newsItem, i) => (
                                <MDBCol size="12" md="4" key={i}>
                                    <MDBCard className="mb-2">
                                        <MDBCardBody>
                                            <Link to={`/news/${newsItem.id}`} className="text-reset">
                                            <MDBCardImage src={"https://iec-cp-public.s3.eu-central-1.amazonaws.com/newsImages/" + newsItem.image.fileName} position='top' alt={newsItem.image.alt} />
                                                <MDBCardTitle>{getLocalizedText(newsItem.name)}</MDBCardTitle>
                                                <MDBCardText>
                                                    {isoToGermanDate(newsItem.createdAt)}
                                                </MDBCardText>
                                            </Link>
                                        </MDBCardBody>
                                    </MDBCard>
                                </MDBCol>
                            )) : (
                                <MDBCol>
                                    <p className="text-muted">{t("No news available")}</p>
                                </MDBCol>
                            )}
                        </MDBRow>
                    )}
                </MDBCol> */}
            </MDBRow>
        </MDBCol>
    </MDBRow>

{/* Ticket Modal */}
<MDBModal open={showTicketModal} onClose={toggleTicketModal}>
    <MDBModalDialog centered>
        <MDBModalContent>
            <MDBModalHeader>
                <MDBModalTitle>{t("Your Ticket")}</MDBModalTitle>
                <MDBBtn className="btn-close" color="none" onClick={toggleTicketModal}></MDBBtn>
            </MDBModalHeader>
            <MDBModalBody>
                {selectedTicket && (
                    <ShowTicket 
                        inModal={true} 
                        ticketId={selectedTicket.id} 
                        handleDeleteTicket={handleDeleteTicket} 
                    />
                )}
            </MDBModalBody>
        </MDBModalContent>
    </MDBModalDialog>
</MDBModal>


{/* Admin Onboarding Modal (V1 - legacy) */}
{isCompanyAdmin && (
  <AdminOnboardingModal
    isOpen={showAdminOnboardingModal}
    onClose={null}
    companyId={adminCompanyId}
    onComplete={handleAdminOnboardingComplete}
  />
)}

{/* Admin Onboarding V2 Modal */}
{isCompanyAdmin && (
  <AdminOnboardingV2Modal
    isOpen={showAdminOnboardingV2Modal}
    onComplete={handleAdminOnboardingV2Complete}
  />
)}
</>
}